import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"

// Properties inspector: one undo snapshot per focus session (avoids flooding
// the undo stack with per-keystroke snapshots while typing).
export default class extends Controller {
  static targets = ["panel", "empty", "width", "height", "heightAbove", "rotation", "planButton", "sizeNote"]

  connect() {
    const handler = () => this.#refresh()
    editorStore.addEventListener("selection:change", handler)
    editorStore.addEventListener("objects:change", handler)
    this._unsubscribe = () => {
      editorStore.removeEventListener("selection:change", handler)
      editorStore.removeEventListener("objects:change", handler)
    }
    this.#refresh()

    // Snapshot on the FIRST actual change in a focus session, not on focus
    // itself — otherwise tab-through-fields pollutes the undo stack with
    // no-op snapshots.
    this._onBlur = (e) => {
      if (!isOurInput(e.target)) return
      this._snapshotted = false
      // If the user left the field empty/invalid, restore the store value so
      // they don't end up looking at a blank input whose value is stale.
      if (!Number.isFinite(parseFloat(e.target.value))) this.#refresh()
    }
    this.element.addEventListener("focusout", this._onBlur)
  }

  disconnect() {
    this._unsubscribe && this._unsubscribe()
    this.element.removeEventListener("focusout", this._onBlur)
  }

  commit() {
    const id = editorStore.selectedId
    if (!id) return
    const obj = editorStore.objects.find((o) => o.id === id)
    if (!obj) return
    // While the user is mid-edit, a cleared field parses as NaN. Keeping the
    // prior store value (instead of falling back to a default) prevents the
    // input from being stomped back to that default on the next refresh.
    const next = (raw, prev) => {
      const n = parseFloat(raw)
      return Number.isFinite(n) ? n : prev
    }
    const prevHeightAbove = obj.heightMetresAboveGround ?? 0
    const prevRotation = obj.rotationDeg ?? 0
    const patch = {
      widthM: next(this.widthTarget.value, obj.widthM),
      heightM: next(this.heightTarget.value, obj.heightM),
      heightMetresAboveGround: next(this.heightAboveTarget.value, prevHeightAbove),
      rotationDeg: next(this.rotationTarget.value, prevRotation),
    }
    const changed =
      patch.widthM !== obj.widthM ||
      patch.heightM !== obj.heightM ||
      patch.heightMetresAboveGround !== prevHeightAbove ||
      patch.rotationDeg !== prevRotation
    if (!changed) return
    if (!this._snapshotted) {
      editorStore.pushSnapshot()
      this._snapshotted = true
    }
    editorStore.updateObject(id, patch, { snapshot: false })
  }

  duplicate() {
    if (editorStore.selectedId) editorStore.duplicateObject(editorStore.selectedId)
  }

  remove() {
    if (editorStore.selectedId) editorStore.removeObject(editorStore.selectedId)
  }

  // Open the house floor-plan editor for the selected house. The overlay
  // (house_mode_controller) listens for this window event.
  editPlan() {
    const id = editorStore.selectedId
    if (!id) return
    window.dispatchEvent(new CustomEvent("house:open", { detail: { id } }))
  }

  #refresh() {
    const id = editorStore.selectedId
    // Reset snapshot session when the selected object changes, so the first
    // edit on a newly-selected object still creates an undo entry.
    const selectionChanged = id !== this._lastSelectedId
    if (selectionChanged) {
      this._snapshotted = false
      this._lastSelectedId = id
    }
    const obj = id && editorStore.objects.find((o) => o.id === id)
    if (!obj) {
      this.panelTarget.classList.add("hidden")
      this.emptyTarget.classList.remove("hidden")
      return
    }
    this.panelTarget.classList.remove("hidden")
    this.emptyTarget.classList.add("hidden")
    // The floor-plan editor only applies to houses.
    const isHouse = obj.type === "house"
    if (this.hasPlanButtonTarget) {
      this.planButtonTarget.classList.toggle("hidden", !isHouse)
    }
    // A house's size is owned by its floor plan (single source of truth). Once a
    // plan exists, width/height are read-only here — change the size in the plan.
    const sizeLocked = isHouse && !!obj.houseDesign
    for (const input of [this.widthTarget, this.heightTarget]) {
      input.disabled = sizeLocked
      input.classList.toggle("opacity-50", sizeLocked)
      input.classList.toggle("cursor-not-allowed", sizeLocked)
    }
    if (this.hasSizeNoteTarget) this.sizeNoteTarget.classList.toggle("hidden", !sizeLocked)
    // Don't overwrite the input the user is currently editing on the same
    // object — their own commit() triggered this refresh, and rewriting its
    // value would move the caret and (for cleared fields) immediately replace
    // their blank with a fallback number. On selection change, always rewrite:
    // the focused input must reflect the newly-selected object, not the old.
    const focused = selectionChanged ? null : document.activeElement
    if (this.widthTarget !== focused) this.widthTarget.value = fixed(obj.widthM)
    if (this.heightTarget !== focused) this.heightTarget.value = fixed(obj.heightM)
    if (this.heightAboveTarget !== focused) this.heightAboveTarget.value = fixed(obj.heightMetresAboveGround ?? 0)
    if (this.rotationTarget !== focused) this.rotationTarget.value = fixed(obj.rotationDeg ?? 0)
  }
}

function fixed(n) {
  return Math.round(n * 10) / 10
}

function isOurInput(el) {
  if (!el || el.tagName !== "INPUT") return false
  return ["width", "height", "heightAbove", "rotation"].some(
    (target) => el.hasAttribute(`data-properties-target`) && el.dataset.propertiesTarget === target
  )
}
